export const DEFAULT_BRANDING = {
  client_id: "default",
  display_name: "MakeMake",
  logo_url: "",
  favicon_url: "",
  primary_color: "#ff8200",
  secondary_color: "#1f2937",
  background_color: "#ffffff",
  text_color: "#161616",
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const MIN_TEXT_CONTRAST = 4.5;
const DEFAULT_FAVICON_HREF = "/favicon.ico";

const BRANDING_CSS_VARIABLES = [
  "--mm-brand-logo-background",
  "--mm-bg",
  "--mm-bg-footer-bar",
  "--mm-text-primary",
  "--mm-button-primary",
  "--mm-button-primary-hover",
  "--mm-button-primary-active",
  "--mm-button-primary-text",
  "--mm-button-tertiary",
  "--mm-button-secondary",
  "--mm-button-secondary-hover",
  "--mm-button-secondary-active",
  "--mm-border",
  "--mm-footer-bar-text",
  "--mm-chart-orange",
  "--mm-chart-orange-stroke",
  "--mm-chart-primary",
  "--mm-chart-secondary",
  "--cds-charts-1-1-1",
  "--cds-charts-1-2-1",
  "--mm-heatmap-thumb-hover",
  "--mm-map-stroke",
  "--cds-button-primary",
  "--cds-button-primary-hover",
  "--cds-button-primary-active",
  "--cds-button-tertiary",
  "--cds-button-tertiary-hover",
  "--cds-button-tertiary-active",
  "--cds-button-secondary",
  "--cds-button-secondary-hover",
  "--cds-button-secondary-active",
  "--cds-focus",
  "--cds-text-primary",
  "--cds-text-secondary",
  "--cds-text-on-color",
  "--cds-icon-on-color",
  "--cds-text-on-color-secondary",
];

export const isHexColor = (value) =>
  typeof value === "string" && HEX_COLOR_PATTERN.test(value);

const DEFAULT_HEATMAP_COLOR_GRADIENT = [
  "#5f7c87",
  "#6f8690",
  "#7f9098",
  "#8f9aa1",
  "#9ea4aa",
  "#a89f9e",
  "#B1A0A1",
  "#c48f72",
  "#d2874c",
  "#e07f26",
  "#ee7700",
];

const hexToRgb = (hex) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")}`;

const relativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
};

export const getContrastRatio = (firstColor, secondColor) => {
  if (!isHexColor(firstColor) || !isHexColor(secondColor)) return 0;

  const firstLuminance = relativeLuminance(firstColor);
  const secondLuminance = relativeLuminance(secondColor);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

const readableTextOn = (backgroundColor) =>
  getContrastRatio(backgroundColor, "#ffffff") >=
  getContrastRatio(backgroundColor, "#000000")
    ? "#ffffff"
    : "#000000";

const adjustColor = (hex, amount) => {
  if (!isHexColor(hex)) return hex;

  const rgb = hexToRgb(hex);
  return rgbToHex({
    r: rgb.r + amount,
    g: rgb.g + amount,
    b: rgb.b + amount,
  });
};

const adjustTextColorForContrast = (backgroundColor, textColor) => {
  if (!isHexColor(backgroundColor) || !isHexColor(textColor)) return textColor;
  if (getContrastRatio(backgroundColor, textColor) >= MIN_TEXT_CONTRAST) {
    return textColor;
  }

  const targetColor = readableTextOn(backgroundColor);
  const step = targetColor === "#ffffff" ? 5 : -5;
  let adjustedColor = textColor;

  for (let i = 0; i < 51; i += 1) {
    adjustedColor = adjustColor(adjustedColor, step);
    if (getContrastRatio(backgroundColor, adjustedColor) >= MIN_TEXT_CONTRAST) {
      return adjustedColor;
    }
  }

  return targetColor;
};

const getAccessibleTextColor = (backgroundColor, preferredTextColor, fallbackTextColor) => {
  if (isHexColor(backgroundColor) && isHexColor(preferredTextColor)) {
    return adjustTextColorForContrast(backgroundColor, preferredTextColor);
  }

  if (
    isHexColor(backgroundColor) &&
    isHexColor(fallbackTextColor) &&
    getContrastRatio(backgroundColor, fallbackTextColor) >= MIN_TEXT_CONTRAST
  ) {
    return fallbackTextColor;
  }

  return readableTextOn(backgroundColor);
};

export const normalizeAssetUrl = (assetUrl) => {
  if (!assetUrl) return "";

  try {
    const url = assetUrl.startsWith('/')
      ? new URL(assetUrl, window.location.origin)
      : new URL(assetUrl);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch (_) {
    return "";
  }
};

const safeCssUrl = (url) =>
  url ? `url("${url.replace(/["\\]/g, "\\$&")}")` : "";

const readCssVariable = (name) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return "";

  return window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
};

const getIconLink = (documentRef) =>
  documentRef.querySelector("link[rel='icon']") ||
  documentRef.querySelector("link[rel='shortcut icon']");

const ensureIconLink = (documentRef) => {
  const existingIconLink = getIconLink(documentRef);
  if (existingIconLink) return existingIconLink;

  const iconLink = documentRef.createElement("link");
  iconLink.rel = "icon";
  documentRef.head?.appendChild(iconLink);

  return iconLink;
};

const getDefaultFaviconHref = (iconLink) => {
  if (!iconLink.dataset.defaultHref) {
    iconLink.dataset.defaultHref =
      iconLink.getAttribute("href") || DEFAULT_FAVICON_HREF;
  }

  return iconLink.dataset.defaultHref;
};

export const getRuntimeChartPalette = (defaultPalette) => {
  const primaryColor = readCssVariable("--mm-chart-primary");
  const secondaryColor = readCssVariable("--mm-chart-secondary");

  if (!isHexColor(primaryColor) || !isHexColor(secondaryColor)) {
    return defaultPalette;
  }

  const remainingDefaultColors = defaultPalette.filter(
    (color) =>
      color.toLowerCase() !== primaryColor.toLowerCase() &&
      color.toLowerCase() !== secondaryColor.toLowerCase()
  );

  return [primaryColor, secondaryColor, ...remainingDefaultColors];
};

export const getRuntimeHeatmapColorGradient = () => {
  const primaryColor = readCssVariable("--mm-chart-primary");
  const secondaryColor = readCssVariable("--mm-chart-secondary");

  if (!isHexColor(primaryColor) || !isHexColor(secondaryColor)) {
    return DEFAULT_HEATMAP_COLOR_GRADIENT;
  }

  return [secondaryColor, primaryColor];
};

export const sanitizeBrandingConfig = (payload = {}) => {
  const merged = {
    ...DEFAULT_BRANDING,
    ...payload,
  };

  const primaryColor = isHexColor(merged.primary_color)
    ? merged.primary_color
    : DEFAULT_BRANDING.primary_color;
  const secondaryColor = isHexColor(merged.secondary_color)
    ? merged.secondary_color
    : DEFAULT_BRANDING.secondary_color;
  const backgroundColor = isHexColor(merged.background_color)
    ? merged.background_color
    : DEFAULT_BRANDING.background_color;
  const textColor = getAccessibleTextColor(
    backgroundColor,
    merged.text_color,
    DEFAULT_BRANDING.text_color
  );

  return {
    client_id: merged.client_id || DEFAULT_BRANDING.client_id,
    display_name: merged.display_name || DEFAULT_BRANDING.display_name,
    logo_url: normalizeAssetUrl(merged.logo_url),
    favicon_url: normalizeAssetUrl(merged.favicon_url),
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    background_color: backgroundColor,
    text_color: textColor,
  };
};

const isDefaultBrandingConfig = (config) =>
  !config.logo_url &&
  !config.favicon_url &&
  config.display_name === DEFAULT_BRANDING.display_name &&
  config.primary_color.toLowerCase() === DEFAULT_BRANDING.primary_color &&
  config.secondary_color.toLowerCase() === DEFAULT_BRANDING.secondary_color &&
  config.background_color.toLowerCase() === DEFAULT_BRANDING.background_color &&
  config.text_color.toLowerCase() === DEFAULT_BRANDING.text_color;

export const getBrandingCssVariables = (branding) => {
  const config = sanitizeBrandingConfig(branding);
  const isDefaultBranding = isDefaultBrandingConfig(config);
  const primaryTextColor = readableTextOn(config.primary_color);
  const secondaryTextColor = readableTextOn(config.secondary_color);
  const focusColor = config.primary_color;

  return {
    "--mm-brand-logo-background": safeCssUrl(config.logo_url),
    "--mm-bg": config.background_color,
    "--mm-bg-footer-bar": config.primary_color,
    "--mm-text-primary": config.text_color,
    "--mm-button-primary": config.primary_color,
    "--mm-button-primary-hover": adjustColor(config.primary_color, -18),
    "--mm-button-primary-active": adjustColor(config.primary_color, -36),
    "--mm-button-primary-text": primaryTextColor,
    "--mm-button-tertiary": focusColor,
    "--mm-button-secondary": config.secondary_color,
    "--mm-button-secondary-hover": adjustColor(config.secondary_color, 20),
    "--mm-button-secondary-active": adjustColor(config.secondary_color, -20),
    "--mm-border": config.secondary_color,
    "--mm-footer-bar-text": primaryTextColor,
    "--mm-chart-orange": config.primary_color,
    "--mm-chart-orange-stroke": adjustColor(config.primary_color, -80),
    "--mm-chart-primary":
      !isDefaultBranding ? config.primary_color : "",
    "--mm-chart-secondary":
      !isDefaultBranding ? config.secondary_color : "",
    "--cds-charts-1-1-1":
      !isDefaultBranding ? config.primary_color : "",
    "--cds-charts-1-2-1":
      !isDefaultBranding ? config.secondary_color : "",
    "--mm-heatmap-thumb-hover": config.primary_color,
    "--mm-map-stroke": config.primary_color,
    "--cds-button-primary": config.primary_color,
    "--cds-button-primary-hover": adjustColor(config.primary_color, -18),
    "--cds-button-primary-active": adjustColor(config.primary_color, -36),
    "--cds-button-tertiary": focusColor,
    "--cds-button-tertiary-hover": focusColor,
    "--cds-button-tertiary-active": adjustColor(config.primary_color, -36),
    "--cds-button-secondary": config.secondary_color,
    "--cds-button-secondary-hover": adjustColor(config.secondary_color, 20),
    "--cds-button-secondary-active": adjustColor(config.secondary_color, -20),
    "--cds-focus": focusColor,
    "--cds-text-primary": config.text_color,
    "--cds-text-secondary": config.text_color,
    "--cds-text-on-color": primaryTextColor,
    "--cds-icon-on-color": primaryTextColor,
    "--cds-text-on-color-secondary": secondaryTextColor,
  };
};

export const applyBrandingToDocument = (branding, documentRef = document) => {
  if (!documentRef?.documentElement) return;

  const variables = getBrandingCssVariables(branding);
  Object.entries(variables).forEach(([name, value]) => {
    if (value) {
      documentRef.documentElement.style.setProperty(name, value);
    } else {
      documentRef.documentElement.style.removeProperty(name);
    }
  });

  const config = sanitizeBrandingConfig(branding);
  documentRef.title = config.display_name;

  const favicon = config.favicon_url || config.logo_url;
  const iconLink = ensureIconLink(documentRef);
  const defaultFavicon = getDefaultFaviconHref(iconLink);

  if (iconLink) {
    iconLink.href = favicon || defaultFavicon;
  }
};

export const clearBrandingFromDocument = (documentRef = document) => {
  if (!documentRef?.documentElement) return;

  BRANDING_CSS_VARIABLES.forEach((name) => {
    documentRef.documentElement.style.removeProperty(name);
  });

  applyBrandingToDocument(DEFAULT_BRANDING, documentRef);
};
